import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Input } from 'ncoded-component-library';
import {
  DataRoomRFICategory,
  DataRoomRFICreationType,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import SingleSelectDropdownFilter from 'components/SingleSelectDropdownFilter';
import SearchIcon from 'icons/Search.icon';
import { getIsRfiCreationTypeChecklist } from '../../../RfiList/components/RfiRequestsTable/utils';
import { UPLOAD_STATUS_FILTER_LABEL_KEYS } from '../../utils';
import RfiSpreadsheetCategoriesTable from './components/RfiSpreadsheetCategoriesTable';
import RfiChecklistCategoriesTable from './components/RfiChecklistCategoriesTable';

import './RfiCategoriesTable.styles.scss';

type RfiCategoriesTableProps = {
  className?: string;
  isLoading?: boolean;
  data: DataRoomRFICategory[];
  totalPages?: number;
  totalItems?: number;
  creationType: DataRoomRFICreationType;
  searchInputValue: string;
  statusFilter: string;
  assignedEmails: string[];
  isReassignEnabled: boolean;
  onCategoryUpdate: (category: DataRoomRFICategory) => void;
  onSearchInputChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onRowClick: (category: DataRoomRFICategory) => void;
};

const RfiCategoriesTable: React.FC<RfiCategoriesTableProps> = (props) => {
  const {
    className,
    isLoading,
    data,
    totalPages,
    totalItems,
    creationType,
    searchInputValue,
    statusFilter,
    assignedEmails,
    isReassignEnabled,
    onCategoryUpdate,
    onSearchInputChange,
    onStatusFilterChange,
    onRowClick,
  } = props;

  const { t } = useTranslation();

  const classes = classNames('rfi-categories-table', className);

  const isChecklist = getIsRfiCreationTypeChecklist(creationType);

  const statusOptions = useMemo(
    () =>
      Object.entries(UPLOAD_STATUS_FILTER_LABEL_KEYS).map(
        ([value, labelKey]) => ({ value, label: t(labelKey) }),
      ),
    [t],
  );

  const statusFilterTriggerTitle = useMemo(
    () => statusOptions.find(({ value }) => value === statusFilter)?.label,
    [statusOptions, statusFilter],
  );

  return (
    <div className={classes}>
      <div className="rfi-categories-table__filters">
        <Input
          placeholder={t(
            isChecklist ? 'searchDocuments' : 'searchDocumentsOrPeople',
          )}
          prefixNode={<SearchIcon />}
          value={searchInputValue}
          onChange={({ target: { value } }) => onSearchInputChange(value)}
        />

        <SingleSelectDropdownFilter
          selectedValue={statusFilter}
          triggerTitle={statusFilterTriggerTitle}
          options={statusOptions}
          onChange={onStatusFilterChange}
        />
      </div>

      {isChecklist ? (
        <RfiChecklistCategoriesTable
          isLoading={isLoading}
          data={data}
          totalPages={totalPages}
          totalItems={totalItems}
          onRowClick={onRowClick}
        />
      ) : (
        <RfiSpreadsheetCategoriesTable
          isLoading={isLoading}
          data={data}
          totalPages={totalPages}
          totalItems={totalItems}
          assignedEmails={assignedEmails}
          isReassignEnabled={isReassignEnabled}
          onCategoryUpdate={onCategoryUpdate}
          onRowClick={onRowClick}
        />
      )}
    </div>
  );
};

export default RfiCategoriesTable;
